/* =============================================================================
   Wheel & tyre visualiser — 2D cross-section, vehicle-aware fitment verdict.
   Geometry (mm, x = outward from the car's hub face, y = radius from axle):
     wheel centreline  xc = spacer − ET
     rim lips          xc ± (width × 12.7 + flange)
     tyre sidewalls    xc ± sectionWidth / 2   (section width corrected for rim width, ETRTO 0.4 rule)
     rim radius        diameter × 12.7,  tyre radius = rim radius + width × profile %
   Poke / inner change are measured against the current (factory) setup and
   judged against the vehicle-class clearance limits stored in fitment-db.json.
============================================================================= */
(function () {
  'use strict';
  var W = window.WWC;
  var root = document.querySelector('[data-visualiser]');
  if (!root || !W) return;
  var q = function (s) { return root.querySelector(s); };
  var LIMITS = { car: [12, 8, 25, 12], perf: [12, 8, 25, 12], tight: [10, 6, 18, 10], suv: [14, 8, 28, 12], '4x4': [30, 6, 60, 10], ev: [12, 6, 22, 10] };
  var DB = null, car = null, carName = '', axle = 'F';
  var canvas = q('[data-vz-canvas]'), ctx = canvas.getContext('2d');
  var css = getComputedStyle(document.documentElement);
  var ACCENT = (css.getPropertyValue('--c-accent') || '#1E6BFF').trim() || '#1E6BFF';

  function field(k) { return q('[data-vz="' + k + '"]'); }
  function num(k) { return parseFloat(field(k).value); }
  function setv(k, v) { var el = field(k); if (!el) return; el.value = v; }

  function parseTyre(s) { var m = String(s || '').match(/(\d{3})\s*\/\s*(\d{2})\s*Z?R\s*(\d{2})/i); return m ? { w: +m[1], a: +m[2], d: +m[3] } : null; }
  // Actual section width on a given rim (ETRTO: ±0.4 × rim-width change from the measuring rim)
  // Measuring-rim and approved-rim ratios by profile (approximates the ETRTO tables:
  // e.g. 255/35 → 8–9.5J on a 9J measuring rim; 265/60 → 7–9J on an 8J measuring rim)
  function ratios(aspect) {
    if (aspect >= 60) return { meas: 0.77, min: 0.66, max: 0.87 };
    if (aspect >= 55) return { meas: 0.78, min: 0.70, max: 0.89 };
    if (aspect >= 50) return { meas: 0.79, min: 0.73, max: 0.91 };
    if (aspect >= 45) return { meas: 0.85, min: 0.77, max: 0.93 };
    if (aspect >= 35) return { meas: 0.89, min: 0.79, max: 0.95 };
    return { meas: 0.9, min: 0.84, max: 0.98 };
  }
  function half(x) { return Math.round(x * 2) / 2; }
  function sectionWidth(nominal, aspect, rim) {
    var meas = half(nominal * ratios(aspect).meas / 25.4);
    return nominal + 0.4 * (rim - meas) * 25.4;
  }
  function rimRange(nominal, aspect) { var r = ratios(aspect); return [half(nominal * r.min / 25.4), half(nominal * r.max / 25.4)]; }
  function geom(w, d, et, tw, ta, sp) {
    var xc = sp - et, half = w * 12.7, sw = sectionWidth(tw, ta, w), rimR = d * 12.7, R = rimR + tw * ta / 100;
    return { w: w, d: d, et: et, tw: tw, ta: ta, sp: sp, xc: xc, lipIn: xc - half - 7, lipOut: xc + half + 7, tyIn: xc - sw / 2, tyOut: xc + sw / 2,
      outer: Math.max(xc + sw / 2, xc + half + 7), inner: Math.min(xc - sw / 2, xc - half - 7), rimR: rimR, R: R, sw: sw };
  }
  function fmtMm(v) { var r = Math.round(v); return (r > 0 ? '+' : r < 0 ? '−' : '±') + Math.abs(r) + ' mm'; }

  /* ---------- compute + verdict ---------- */
  function compute() {
    var cur = geom(num('sw'), num('sd'), num('se'), num('stw'), num('sta'), 0);
    var nw = geom(num('nw'), num('nd'), num('ne'), num('ntw'), num('nta'), num('sp'));
    var cls = car && car.cls ? car.cls : 'car';
    // Per-vehicle clearance (researched) overrides the class default; limR is the rear axle when it differs.
    var L = (car && ((axle === 'R' && car.limR) || car.lim)) || (DB && DB.LIMITS && DB.LIMITS[cls]) || LIMITS[cls] || LIMITS.car;
    var dOut = nw.outer - cur.outer, dIn = cur.inner - nw.inner;
    var depth = nw.w * 12.7 - nw.et, bs = (nw.w + 1) * 12.7 + nw.et;
    var odPct = (nw.R / cur.R - 1) * 100, rr = rimRange(nw.tw, nw.ta);
    var profile = nw.w > rr[1] + 0.5 ? 'Heavy stretch' : nw.w > rr[1] ? 'Stretched' : nw.w < rr[0] - 0.5 ? 'Heavy bulge' : nw.w < rr[0] ? 'Bulged' : 'Ideal fit';
    var level = 0, reasons = [];
    function flag(l, t) { level = Math.max(level, l); reasons.push({ l: l, t: t }); }
    if (dOut > L[2]) flag(2, 'Sits ' + Math.round(dOut) + ' mm further out than ' + (car ? 'factory' : 'your current setup') + ' — past what the guards take (max +' + L[2] + ' mm). Expect rubbing or poke.');
    else if (dOut > L[0]) flag(1, 'Sits ' + Math.round(dOut) + ' mm further out — over the street limit (+' + L[0] + ' mm). Usually needs a guard roll and/or negative camber.');
    if (dIn > L[3]) flag(2, 'Sits ' + Math.round(dIn) + ' mm closer to the suspension — likely to hit the strut, spring or inner arch.');
    else if (dIn > L[1]) flag(1, Math.round(dIn) + ' mm closer to the suspension than ' + (car ? 'factory' : 'current') + ' — check strut and coilover clearance.');
    if (car && nw.d < (axle === 'R' ? car.stockR.d : car.stockF.d)) flag(1, 'Smaller diameter than factory — may not clear the brakes. We check caliper clearance before production.');
    if (Math.abs(odPct) > 5) flag(2, 'Overall diameter changes ' + odPct.toFixed(1) + '% — speedo, ABS and roadworthiness are affected.');
    else if (Math.abs(odPct) > 3) flag(1, 'Overall diameter changes ' + odPct.toFixed(1) + '% — your speedo will read noticeably ' + (odPct > 0 ? 'low' : 'high') + '.');
    if (/Heavy/.test(profile)) flag(1, profile + ' — a ' + nw.tw + ' tyre suits a ' + rr[0] + '–' + rr[1] + 'J rim. ' + (profile === 'Heavy stretch' ? 'Stretched tyres can unseat under load.' : 'Bulged sidewalls feel vague and can rub the arch.'));
    if (!reasons.length) reasons.push({ l: 0, t: 'Within street limits for ' + (car ? carName : 'your current setup') + ': ' + fmtMm(dOut) + ' at the outside, ' + fmtMm(dIn).replace('+', '+') + ' toward the suspension.' });
    if (car && level < 2) reasons.push({ l: 0, t: 'Needs ' + car.bolt + ' bolt pattern, ' + car.bore + ' centre bore, ' + car.stud + ' — we machine forged sets to suit.' });
    return { cur: cur, nw: nw, L: L, dOut: dOut, dIn: dIn, depth: depth, bs: bs, odPct: odPct, rr: rr, profile: profile, level: level, reasons: reasons };
  }

  /* ---------- render text ---------- */
  function out(k, v) { var el = q('[data-vz-out="' + k + '"]'); if (el) el.textContent = v; }
  function show(k, v) { var el = q('[data-vz-show="' + k + '"]'); if (el) el.textContent = v; }
  function update() {
    show('nw', num('nw') + 'J'); show('ne', 'ET' + num('ne')); show('sp', num('sp') + ' mm');
    // filled slider tracks
    root.querySelectorAll('input[type=range]').forEach(function (i) { i.style.setProperty('--p', ((i.value - i.min) / (i.max - i.min) * 100) + '%'); });
    var code = num('ntw') + '/' + num('nta') + 'R' + num('nd');
    q('[data-vz-tyrecode]').textContent = code;
    var r = compute();
    out('depth', Math.max(0, r.depth).toFixed(1) + ' mm');
    out('bs', r.bs.toFixed(1) + ' mm');
    out('out', fmtMm(r.dOut)); out('in', fmtMm(r.dIn));
    out('dia', Math.round(r.nw.R * 2) + ' mm'); out('speedo', (r.odPct >= 0 ? '+' : '') + r.odPct.toFixed(1) + '% vs current');
    out('profile', r.profile); out('rimrange', 'Approved rim ' + r.rr[0] + '–' + r.rr[1] + 'J');
    var badge = q('[data-vz-badge]'), box = q('[data-vz-verdict]');
    var labels = car || q('[data-vz-stockbox]').dataset.edited ? ['Fits — street', 'Aggressive', 'Too much'] : ['Compared with default setup', 'Aggressive', 'Too much'];
    badge.textContent = labels[r.level];
    box.dataset.level = r.level;
    q('[data-vz-reasons]').innerHTML = r.reasons.map(function (x) { return '<li data-l="' + x.l + '">' + W.esc(x.t) + '</li>'; }).join('');
    var cta = q('[data-vz-cta]');
    if (cta) {
      var u = new URL(cta.getAttribute('href'), location.origin);
      u.searchParams.set('dia', num('nd')); u.searchParams.set('width', num('nw')); u.searchParams.set('et', num('ne'));
      if (carName) u.searchParams.set('vehicle', carName);
      cta.href = u.pathname + u.search;
    }
    canvas.setAttribute('aria-label', 'Cross-section: ' + num('nd') + 'x' + num('nw') + ' ET' + num('ne') + ' with ' + code + '. ' + labels[r.level] + '. ' + r.reasons[0].t);
    draw(r);
  }

  /* ---------- canvas ---------- */
  // Slide between setups instead of jumping: the new wheel's geometry is tweened over ~220 ms
  var shown = null, tween = 0;
  var TWEEN_KEYS = ['xc', 'lipIn', 'lipOut', 'tyIn', 'tyOut', 'outer', 'inner', 'rimR', 'R', 'sp', 'w', 'et'];
  function draw(r) {
    var target = r.nw, from = shown, t0 = performance.now();
    cancelAnimationFrame(tween);
    if (!from || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { shown = target; return paint(r); }
    (function step(now) {
      var k = Math.min(1, (now - t0) / 220), e = 1 - Math.pow(1 - k, 3), g = {};
      for (var key in target) g[key] = target[key];
      TWEEN_KEYS.forEach(function (key) { g[key] = from[key] + (target[key] - from[key]) * e; });
      shown = g;
      paint(Object.assign({}, r, { nw: g }));
      if (k < 1) tween = requestAnimationFrame(step); else shown = target;
    })(t0);
  }
  function paint(r) {
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    var cw = canvas.clientWidth || 640, ch = Math.max(260, Math.min(440, cw * 0.62));
    canvas.style.height = ch + 'px';
    canvas.width = Math.round(cw * dpr); canvas.height = Math.round(ch * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cw, ch);
    var c = r.cur, n = r.nw, L = r.L;
    var guardS = c.outer + L[0], guardM = c.outer + L[2], strutS = c.inner - L[1], strutM = c.inner - L[3];
    var xmin = Math.min(c.inner, n.inner, strutM, 0) - 30, xmax = Math.max(c.outer, n.outer, guardM, n.sp) + 30;
    var hubY = Math.max(60, Math.min(c.rimR, n.rimR) * 0.32);
    var ymin = hubY - 50, ymax = Math.max(c.R, n.R) + 40;
    var s = Math.min((cw - 24) / (xmax - xmin), (ch - 24) / (ymax - ymin));
    var ox = (cw - (xmax - xmin) * s) / 2, oy = ch - (ch - (ymax - ymin) * s) / 2;
    var X = function (x) { return ox + (x - xmin) * s; }, Y = function (y) { return oy - (y - ymin) * s; };
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    ctx.font = '700 11px ' + (css.getPropertyValue('--font-body') || 'system-ui, sans-serif'); ctx.textAlign = 'center';

    // engineering grid, 25 mm
    ctx.save(); ctx.strokeStyle = 'rgba(15,23,42,.05)'; ctx.lineWidth = 1;
    for (var gx = Math.ceil(xmin / 25) * 25; gx <= xmax; gx += 25) { ctx.beginPath(); ctx.moveTo(X(gx), Y(ymin)); ctx.lineTo(X(gx), Y(ymax)); ctx.stroke(); }
    for (var gy = Math.ceil(ymin / 25) * 25; gy <= ymax; gy += 25) { ctx.beginPath(); ctx.moveTo(X(xmin), Y(gy)); ctx.lineTo(X(xmax), Y(gy)); ctx.stroke(); }
    ctx.restore();

    // labels sit in small pills and step down a row when they'd collide
    var placed = [];
    function pill(text, cx, cy, color) {
      var w = ctx.measureText(text).width + 12, h = 18, row = 0;
      while (placed.some(function (p) { return Math.abs(p.x - cx) < (p.w + w) / 2 + 4 && Math.abs(p.y - (cy + row * (h + 4))) < h; }) && row < 4) row++;
      var y = cy + row * (h + 4);
      placed.push({ x: cx, y: y, w: w });
      ctx.save();
      ctx.fillStyle = '#fff'; ctx.strokeStyle = color; ctx.lineWidth = 1.2;
      ctx.beginPath(); if (ctx.roundRect) ctx.roundRect(cx - w / 2, y - h / 2, w, h, 9); else ctx.rect(cx - w / 2, y - h / 2, w, h);
      ctx.fill(); ctx.stroke();
      ctx.fillStyle = color; ctx.textBaseline = 'middle'; ctx.fillText(text, cx, y + .5);
      ctx.restore();
    }
    // limit lines
    function vline(x, y0, y1, color, dash, label) {
      ctx.save(); ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.setLineDash(dash || []);
      ctx.beginPath(); ctx.moveTo(X(x), Y(y0)); ctx.lineTo(X(x), Y(y1)); ctx.stroke(); ctx.restore();
      if (label) pill(label, X(x), Y(y1) + 12, color);
    }
    var topY = ymax - 6, archY = c.R + 22;
    if (car || q('[data-vz-stockbox]').dataset.edited) {
      vline(guardS, c.R * 0.62, topY - 14, '#D97706', [6, 5], 'Street', false);
      vline(guardM, c.R * 0.55, topY - 14, '#DC2626', [], 'Guard max', false);
      vline(strutM, c.rimR + 10, topY - 14, '#DC2626', [], 'Suspension', false);
      vline(strutS, c.rimR + 10, topY - 30, '#D97706', [6, 5], '', false);
      // arch line
      ctx.save(); ctx.strokeStyle = 'rgba(17,24,39,.25)'; ctx.lineWidth = 1.5; ctx.setLineDash([2, 4]);
      ctx.beginPath(); ctx.moveTo(X(strutM), Y(archY)); ctx.lineTo(X(guardM), Y(archY)); ctx.stroke(); ctx.restore();
    }

    // vehicle hub face + spacer
    ctx.save(); ctx.strokeStyle = 'rgba(30,107,255,.6)'; ctx.setLineDash([4, 4]); ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(X(0), Y(ymin + 4)); ctx.lineTo(X(0), Y(hubY + 60)); ctx.stroke(); ctx.restore();
    if (n.sp > 0) { ctx.fillStyle = 'rgba(107,114,128,.35)'; ctx.fillRect(X(0), Y(hubY + 34), X(n.sp) - X(0), (Y(hubY - 34) - Y(hubY + 34))); }

    function tyrePath(g) {
      var side = g.R - g.rimR, bIn = g.lipIn + 9, bOut = g.lipOut - 9;
      ctx.beginPath();
      ctx.moveTo(X(bIn), Y(g.rimR + 8));
      ctx.bezierCurveTo(X(g.tyIn), Y(g.rimR + side * 0.15), X(g.tyIn), Y(g.R - side * 0.3), X(g.tyIn + side * 0.18), Y(g.R));
      ctx.lineTo(X(g.tyOut - side * 0.18), Y(g.R));
      ctx.bezierCurveTo(X(g.tyOut), Y(g.R - side * 0.3), X(g.tyOut), Y(g.rimR + side * 0.15), X(bOut), Y(g.rimR + 8));
    }
    function wheelPath(g) {
      ctx.beginPath();
      ctx.moveTo(X(g.lipIn), Y(g.rimR + 14)); ctx.lineTo(X(g.lipIn), Y(g.rimR)); ctx.lineTo(X(g.lipOut), Y(g.rimR)); ctx.lineTo(X(g.lipOut), Y(g.rimR + 14));
      // face / spokes: outer lip down to the mounting pad (concave dish)
      ctx.moveTo(X(g.lipOut - 6), Y(g.rimR));
      ctx.bezierCurveTo(X(g.lipOut - (g.lipOut - g.sp) * 0.3), Y(g.rimR - (g.rimR - hubY) * 0.55), X(g.sp + (g.lipOut - g.sp) * 0.08), Y(hubY + (g.rimR - hubY) * 0.3), X(g.sp), Y(hubY));
      ctx.moveTo(X(g.sp), Y(hubY - 34)); ctx.lineTo(X(g.sp), Y(hubY + 34));
    }
    // ghost (current / factory)
    ctx.save(); ctx.setLineDash([5, 5]); ctx.strokeStyle = 'rgba(107,114,128,.85)'; ctx.lineWidth = 2;
    tyrePath(c); ctx.stroke(); wheelPath(c); ctx.stroke(); ctx.restore();
    // new tyre: shaded rubber with tread grooves
    ctx.save(); tyrePath(n); ctx.lineTo(X(n.lipIn + 9), Y(n.rimR + 8)); ctx.closePath();
    var tg = ctx.createLinearGradient(0, Y(n.R), 0, Y(n.rimR));
    tg.addColorStop(0, '#2A2D33'); tg.addColorStop(.5, '#17191D'); tg.addColorStop(1, '#0E0F12');
    ctx.fillStyle = tg; ctx.fill(); ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.stroke();
    ctx.clip();
    ctx.strokeStyle = 'rgba(255,255,255,.10)'; ctx.lineWidth = 3;
    var tw = n.tyOut - n.tyIn;
    [.22, .42, .58, .78].forEach(function (f) { var gx = n.tyIn + tw * f; ctx.beginPath(); ctx.moveTo(X(gx), Y(n.R + 2)); ctx.lineTo(X(gx), Y(n.R - (n.R - n.rimR) * .14)); ctx.stroke(); });
    ctx.restore();
    // new wheel: metal barrel and a solid spoke face, outlined in the accent colour
    var mg = ctx.createLinearGradient(X(n.lipIn), 0, X(n.lipOut), 0);
    mg.addColorStop(0, '#9AA5B4'); mg.addColorStop(.5, '#E6EBF1'); mg.addColorStop(1, '#AEB8C5');
    ctx.save(); ctx.fillStyle = mg; ctx.strokeStyle = ACCENT; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.rect(X(n.lipIn), Y(n.rimR), X(n.lipOut) - X(n.lipIn), Y(n.rimR - 9) - Y(n.rimR)); ctx.fill(); ctx.stroke();
    [n.lipIn, n.lipOut].forEach(function (lx) { ctx.beginPath(); ctx.rect(X(lx) - 3, Y(n.rimR + 14), 6, Y(n.rimR - 9) - Y(n.rimR + 14)); ctx.fill(); ctx.stroke(); });
    var faceT = 14, a0 = n.lipOut - 6, b0 = n.sp;
    ctx.beginPath();
    ctx.moveTo(X(a0), Y(n.rimR - 9));
    ctx.bezierCurveTo(X(a0 - (a0 - b0) * 0.3), Y(n.rimR - (n.rimR - hubY) * 0.55), X(b0 + (a0 - b0) * 0.08), Y(hubY + (n.rimR - hubY) * 0.3), X(b0), Y(hubY + 30));
    ctx.lineTo(X(b0 - faceT), Y(hubY + 30));
    ctx.bezierCurveTo(X(b0 - faceT + (a0 - b0) * 0.08), Y(hubY + (n.rimR - hubY) * 0.36), X(a0 - faceT - (a0 - b0) * 0.3), Y(n.rimR - (n.rimR - hubY) * 0.5), X(a0 - faceT * 1.6), Y(n.rimR - 9));
    ctx.closePath();
    var fg = ctx.createLinearGradient(0, Y(n.rimR), 0, Y(hubY));
    fg.addColorStop(0, '#C9D2DD'); fg.addColorStop(1, '#8D99A8');
    ctx.fillStyle = fg; ctx.fill(); ctx.stroke();
    ctx.restore();
    // mounting pad on the hub face
    ctx.save(); ctx.fillStyle = ACCENT; ctx.beginPath(); ctx.rect(X(n.sp) - 4, Y(hubY + 34), 8, Y(hubY - 34) - Y(hubY + 34)); ctx.fill(); ctx.restore();

    // centreline + dimensions
    ctx.save(); ctx.strokeStyle = 'rgba(107,114,128,.7)'; ctx.setLineDash([3, 4]); ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(X(n.xc), Y(hubY - 40)); ctx.lineTo(X(n.xc), Y(n.R + 18)); ctx.stroke(); ctx.restore();
    function dim(x0, x1, y, label, color) {
      ctx.save(); ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(X(x0), Y(y)); ctx.lineTo(X(x1), Y(y)); ctx.moveTo(X(x0), Y(y) - 5); ctx.lineTo(X(x0), Y(y) + 5); ctx.moveTo(X(x1), Y(y) - 5); ctx.lineTo(X(x1), Y(y) + 5); ctx.stroke();
      ctx.restore();
      pill(label, (X(x0) + X(x1)) / 2, Y(y) - 14, color);
    }
    if (Math.abs(n.et) > 0.5) dim(n.xc, n.sp, n.R + 18, 'ET' + n.et, '#374151');
    if (r.depth > 0) dim(n.sp, n.lipOut - 7, hubY - 30, 'Concavity ' + r.depth.toFixed(0) + ' mm', ACCENT);
    // poke marker
    if (Math.abs(r.dOut) >= 1) {
      var col = r.dOut > L[2] ? '#DC2626' : r.dOut > L[0] ? '#D97706' : '#15803D';
      dim(c.outer, n.outer, (c.R + n.R) / 2 - (n.R - n.rimR) * 0.5, fmtMm(r.dOut), col);
    }
  }

  /* ---------- vehicles ---------- */
  function stockFor(a) {
    var st = a === 'R' ? car.stockR : car.stockF, ty = parseTyre(a === 'R' ? (car.tyreR || car.tyreF) : car.tyreF) || { w: Math.round(st.w * 25.4 / 0.88 / 10) * 10 + 5, a: 40 };
    return { w: st.w, d: st.d, et: st.et, tw: ty.w, ta: ty.a };
  }
  function applyStock(reset) {
    var s = stockFor(axle);
    setv('sw', s.w); setv('sd', s.d); setv('se', s.et); setv('stw', s.tw); setv('sta', s.ta);
    if (reset) { setv('nw', s.w); setv('nd', s.d); setv('ne', s.et); setv('ntw', s.tw); setv('nta', s.ta); setv('sp', 0); }
    var line = car ? 'Factory: ' + car.stockF.d + 'x' + car.stockF.w + ' ET' + car.stockF.et + ' · ' + car.tyreF +
      (car.stockR && (car.stockR.w !== car.stockF.w || car.stockR.et !== car.stockF.et || car.stockR.d !== car.stockF.d || car.tyreR !== car.tyreF) ? '  |  Rear ' + car.stockR.d + 'x' + car.stockR.w + ' ET' + car.stockR.et + ' · ' + (car.tyreR || car.tyreF) : '') + ' — check your tyre placard; trims vary.' : '';
    q('[data-vz-stockline]').textContent = line;
    q('[data-vz-stocksummary]').textContent = '(' + s.d + 'x' + s.w + ' ET' + s.et + ', ' + s.tw + '/' + s.ta + ')';
    update();
  }
  function setCar(mk, md, yr) {
    if (!DB || !DB.DB[mk] || !DB.DB[mk][md] || !DB.DB[mk][md][yr]) { car = null; carName = ''; return; }
    car = DB.DB[mk][md][yr]; carName = mk + ' ' + md + ' (' + yr + ')';
    var stag = car.stockR && (car.stockR.w !== car.stockF.w || car.stockR.d !== car.stockF.d || car.stockR.et !== car.stockF.et || (car.tyreR && car.tyreR !== car.tyreF));
    q('[data-vz-axle]').hidden = !stag; axle = 'F';
    q('[data-vz-looks]').hidden = false;
    root.querySelectorAll('[data-look]').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
    root.querySelectorAll('[data-axle]').forEach(function (b) { b.setAttribute('aria-checked', b.dataset.axle === 'F'); });
    applyStock(true);
  }
  var selects = W.vehicleSelects(q('[data-vz-car]'), {
    onChange: function (v) { if (v.make && v.model && v.year) setCar(v.make, v.model, v.year); else { car = null; carName = ''; q('[data-vz-stockline]').textContent = ''; update(); } }
  });
  root.querySelector('[data-vz-axle]').addEventListener('click', function (e) {
    var b = e.target.closest('[data-axle]'); if (!b || !car) return;
    axle = b.dataset.axle;
    root.querySelectorAll('[data-axle]').forEach(function (x) { x.setAttribute('aria-checked', x === b); });
    applyStock(true);
  });
  // Quick looks: factory, or the middle of the car's researched street / aggressive range, with a
  // tyre that keeps the overall diameter close to factory
  function axleRange(str) {
    var parts = String(str || '').split('/');
    var part = parts.length > 1 ? (axle === 'R' ? parts.filter(function (x) { return /\bR\b/.test(x); })[0] || parts[1] : parts[0]) : parts[0];
    return W.fit.midRange(part);
  }
  function pickOption(name, v) {
    var sel = q('[data-vz="' + name + '"]'), best = null;
    Array.prototype.forEach.call(sel.options, function (o) { if (best === null || Math.abs(o.value - v) < Math.abs(best - v)) best = +o.value; });
    sel.value = best;
  }
  q('[data-vz-looks]').addEventListener('click', function (e) {
    var b = e.target.closest('[data-look]'); if (!b || !car) return;
    var st = stockFor(axle), src = b.dataset.look === 'agg' ? car.agg : b.dataset.look === 'street' ? car.street : null;
    var d = src ? Math.round(axleRange(src.d) || st.d) : st.d;
    var w = src ? Math.round((axleRange(src.w) || st.w) * 2) / 2 : st.w;
    var et = src ? Math.round(axleRange(src.et) || st.et) : st.et;
    var od = st.d * 25.4 + 2 * st.tw * st.ta / 100;
    var tw = src ? Math.max(155, Math.min(355, st.tw + (w - st.w) * 20)) : st.tw;
    pickOption('nd', d); setv('nw', w); setv('ne', et); setv('sp', 0); pickOption('ntw', tw);
    pickOption('nta', src ? ((od - num('nd') * 25.4) / 2) / num('ntw') * 100 : st.ta);
    root.querySelectorAll('[data-look]').forEach(function (x) { x.setAttribute('aria-pressed', String(x === b)); });
    update();
  });
  root.addEventListener('input', function (e) {
    if (e.target.matches('[data-vz]')) root.querySelectorAll('[data-look]').forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
  });
  q('[data-vz-reset]').addEventListener('click', function () { if (car) applyStock(true); else { setv('nw', num('sw')); setv('nd', num('sd')); setv('ne', num('se')); setv('ntw', num('stw')); setv('nta', num('sta')); setv('sp', 0); update(); } });
  root.addEventListener('input', function (e) {
    if (e.target.matches('[data-vz="sw"],[data-vz="sd"],[data-vz="se"],[data-vz="stw"],[data-vz="sta"]')) q('[data-vz-stockbox]').dataset.edited = '1';
    if (e.target.matches('[data-vz]')) update();
  });
  root.addEventListener('change', function (e) { if (e.target.matches('select[data-vz]')) update(); });
  // fitment finder → visualiser
  document.addEventListener('wwc:vehicle', function (e) {
    var v = e.detail; if (!v) return;
    selects.set({ make: v.make, model: v.model, year: v.year }).then(function () { setCar(v.make, v.model, v.year); });
  });
  var resizeT; window.addEventListener('resize', function () { clearTimeout(resizeT); resizeT = setTimeout(update, 120); });

  W.loadFitment().then(function (d) {
    DB = d;
    selects.ensure();
    var qp = new URLSearchParams(location.search), g = W.getGarage && W.getGarage();
    var pick = qp.get('make') ? { make: qp.get('make'), model: qp.get('model'), year: qp.get('year') } : g;
    if (pick && pick.make) selects.set(pick).then(function () { var v = selects.get(); if (v.make && v.model && v.year) setCar(v.make, v.model, v.year); });
  }).catch(function () {});
  update();
})();
